/**
 * Luli For Babies — "Combinações Prontinhas" (réplica do UpsellKitsCarousel do site Lovable).
 *
 * Mostra os kits que contêm a estampa do produto atual. No site antigo isso era uma
 * Edge Function no Supabase com uma lista fixa de ~60 estampas no código; aqui a
 * própria busca da Shopify resolve, porque ela indexa a descrição do produto.
 */

class LuliCombinations extends HTMLElement {
  connectedCallback() {
    this.handle = this.dataset.handle;
    this.limit = Math.min(parseInt(this.dataset.limit || '10', 10), 10); // a API não devolve mais que 10
    this.prefixes = (this.dataset.prefixes || '')
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean);

    const pattern = this.extractPattern(this.dataset.title || '');
    if (!pattern) {
      this.hide();
      return;
    }

    // A seção fica abaixo da dobra na PDP e faz 1 fetch de busca. Adiar até ela
    // se aproximar da viewport tira essa requisição do caminho crítico da
    // página de produto, sem mudar o que aparece.
    //
    // Observa o wrapper da seção, não este elemento: ele nasce [hidden]
    // (display:none), e elemento sem caixa de layout nunca interseciona —
    // observá-lo direto trava o load para sempre. O wrapper fica com altura
    // zero, mas mantém a posição na página, então serve de sentinela.
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries, obs) => {
        if (entries.some((e) => e.isIntersecting)) {
          obs.disconnect();
          this.load(pattern);
        }
      }, { rootMargin: '400px' });
      io.observe(this.parentElement || this);
    } else {
      this.load(pattern);
    }
  }

  /**
   * Extrai o nome da estampa do título do produto.
   *   "Fraldas Margaridas"            -> "Margaridas"   (remove o prefixo)
   *   "Margaridas - Kit Fraldinhas"   -> "Margaridas"   (corta no traço)
   *   "Cogumelinho- Kit Fraldinhas"   -> "Cogumelinho"  (traço sem espaço)
   *   "Duplinha Azul"                 -> "Azul"         (remove o prefixo)
   * @param {string} title
   * @returns {string} estampa, ou '' se não der para deduzir
   */
  extractPattern(title) {
    let value = title.trim();

    // 1) Se há traço, a estampa costuma vir antes dele
    const dashIndex = value.search(/\s*-\s*/);
    if (dashIndex > 0) value = value.slice(0, dashIndex).trim();

    // 2) Remove prefixos de tipo de produto ("Fraldas", "Duplinha"…)
    for (const prefix of this.prefixes) {
      const re = new RegExp('^' + prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s+', 'i');
      if (re.test(value)) {
        value = value.replace(re, '').trim();
        break;
      }
    }

    // Títulos que viraram só o tipo de produto não têm estampa utilizável
    return value.length > 1 ? value : '';
  }

  async load(pattern) {
    const url =
      '/search/suggest?q=' +
      encodeURIComponent(pattern) +
      '&resources[type]=product' +
      '&resources[limit]=' + this.limit +
      '&resources[options][fields]=body' +
      '&section_id=luli-combinations';

    let html;
    try {
      const response = await fetch(url);
      if (!response.ok) return this.hide();
      html = await response.text();
    } catch (error) {
      return this.hide();
    }

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const cards = Array.from(doc.querySelectorAll('.luli-combo-card'))
      // fora o próprio produto que está sendo visto
      .filter((card) => card.dataset.handle !== this.handle);

    // sem duplicados (o catálogo tem produtos de mesmo nome)
    const seen = new Set();
    const unique = cards.filter((card) => {
      if (seen.has(card.dataset.handle)) return false;
      seen.add(card.dataset.handle);
      return true;
    });

    // do mais barato para o mais caro, como no site antigo
    unique.sort((a, b) => Number(a.dataset.price || 0) - Number(b.dataset.price || 0));

    if (unique.length === 0) return this.hide();

    const list = this.querySelector('[ref="list"]');
    if (!list) return this.hide();
    list.replaceChildren(...unique);

    this.hidden = false;
    this.setupArrows(list);
  }

  setupArrows(list) {
    const prev = this.querySelector('[ref="prev"]');
    const next = this.querySelector('[ref="next"]');
    if (!prev || !next) return;

    const step = () => list.querySelector('.luli-combo-card')?.getBoundingClientRect().width ?? 220;
    prev.addEventListener('click', () => list.scrollBy({ left: -step() * 2, behavior: 'smooth' }));
    next.addEventListener('click', () => list.scrollBy({ left: step() * 2, behavior: 'smooth' }));

    const sync = () => {
      const max = list.scrollWidth - list.clientWidth - 2;
      prev.disabled = list.scrollLeft <= 0;
      next.disabled = list.scrollLeft >= max;
      const overflows = list.scrollWidth > list.clientWidth + 2;
      prev.hidden = !overflows;
      next.hidden = !overflows;
    };
    list.addEventListener('scroll', sync, { passive: true });
    new ResizeObserver(sync).observe(list);
    sync();
  }

  hide() {
    this.hidden = true;
  }
}

if (!customElements.get('luli-combinations')) {
  customElements.define('luli-combinations', LuliCombinations);
}
